// VLA PDF Generation Engine V2
import { 
  PDFDocument, 
  StandardFonts, 
  PDFName, 
  PDFDict, 
  PDFArray, 
  PDFString, 
  PDFHexString, 
  rgb, 
  PDFTextField, 
  PDFCheckBox, 
  PDFRadioGroup, 
  PDFNumber, 
  PDFBool 
} from 'pdf-lib';

function sanitizeTextForPDF(text) {
  if (text === null || text === undefined) return '';
  const str = String(text);
  const regex = /[^\x20-\x7E\n\r\t\u00A0-\u00FF\u20AC\u201A\u0192\u201E\u2026\u2020\u2021\u02C6\u2030\u0160\u2039\u0152\u017D\u2018\u2019\u201C\u201D\u2022\u2013\u2014\u02DC\u2122\u0161\u203A\u0153\u017E\u0178]/g;
  return str.replace(regex, '?');
}

const originalSetText = PDFTextField.prototype.setText;
PDFTextField.prototype.setText = function (text) {
  return originalSetText.call(this, sanitizeTextForPDF(text));
};

function splitTextIntoLines(text, maxLen, maxLines = 3) {
  if (!text) return Array(maxLines).fill("");
  const words = String(text).split(/\s+/);
  const lines = [];
  let currentLine = "";
  
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (!word) continue;
    
    if (word.length > maxLen) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = "";
      }
      lines.push(word.substring(0, maxLen));
      const remainingWord = word.substring(maxLen);
      words.splice(i + 1, 0, remainingWord);
      continue;
    }
    
    if ((currentLine + (currentLine ? " " : "") + word).length <= maxLen) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
      if (lines.length === maxLines - 1) {
        const remaining = words.slice(i).join(" ");
        lines.push(remaining.substring(0, maxLen));
        currentLine = "";
        break;
      }
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }
  while (lines.length < maxLines) {
    lines.push("");
  }
  return lines.slice(0, maxLines);
}
function isPDFTextField(field) {
  if (!field) return false;
  if (field instanceof PDFTextField) return true;
  return typeof field.setText === 'function' && typeof field.getText === 'function';
}

function isPDFCheckBox(field) {
  if (!field) return false;
  if (field instanceof PDFCheckBox) return true;
  return typeof field.isChecked === 'function' && typeof field.check === 'function';
}

function isPDFRadioGroup(field) {
  if (!field) return false;
  if (field instanceof PDFRadioGroup) return true;
  return typeof field.getOptions === 'function' && typeof field.select === 'function';
}

const normalize = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, '');

// COMPREHENSIVE TRANSLATION MAP (matching backend server.js)
const TRANSLATION_MAP = {
    // Page 1
    'first_name': 'patient_first_name',
    'middle_name': 'patient_middle_name',
    'last_name': 'patient_last_name',
    'home_address': 'patient_home_address_line_1',
    'postal_address': 'patient_postal_address_line_1',
    'email': 'patient_email_address',
    'home_phone': 'patient_home_phone',
    'mobile_phone': 'patient_mobile_phone',
    'work_phone': 'patient_work_phone',
    'other_phone': 'patient_other_contact_phone',
    'country_of_birth': 'patient_country_of_birth',
    'arrival_year': 'patient_year_arrived_in_australia',
    'other_names_list': 'patient_other_names_used',
    'custody_facility': 'custody_detention_location',
    'corrective_services_id': 'corrective_services_id',
    // Page 2
    'which_language': 'patient_language_other_than_english',
    'interpreter_language': 'patient_interpreter_language',
    'disability_other': 'patient_disability_other',
    'support_other': 'patient_support_other',
    // Section 12: Partner
    'partner_first_name': 'partner_first_name',
    'partner_middle_name': 'partner_middle_name',
    'partner_last_name': 'partner_last_name',
    'partner_dob': 'partner_date_of_birth',
    // Section 13: Dependants
    'child_support_count': 'children_maintenance_dependants_payment_for_count',
    // Page 6-8 (Income)
    'income_pensions_you': 'income_pensions_benefits_allowances_you',
    'income_pensions_partner': 'income_pensions_benefits_allowances_partner',
    'income_pensions_freq': 'income_pensions_benefits_allowances_frequency',
    'income_employment_you': 'income_employment_you',
    'income_employment_partner': 'income_employment_partner',
    'income_employment_freq': 'income_employment_frequency',
    'income_business_you': 'income_business_self_employed_you',
    'income_business_partner': 'income_business_self_employed_partner',
    'income_business_freq': 'income_business_self_employed_frequency',
    'income_child_support_you': 'income_child_support_you',
    'income_child_support_partner': 'income_child_support_partner',
    'income_child_support_freq': 'income_child_support_frequency',
    'income_other_you': 'income_other_you',
    'income_other_partner': 'income_other_partner',
    'income_other_freq': 'income_other_frequency',
    'income_other_type': 'income_other_type',
    'income_total_you': 'income_total_you',
    'income_total_partner': 'income_total_partner',
    'income_total_freq': 'income_total_frequency',
    // Expenses
    'expense_tax_you': 'expenses_income_tax_your_partner',
    'expense_tax_partner': 'expenses_income_tax_partner',
    'expense_tax_freq': 'expenses_income_tax_frequency',
    'expense_rent_you': 'expenses_rent_your_partner',
    'expense_rent_partner': 'expenses_rent_partner',
    'expense_rent_freq': 'expenses_rent_frequency',
    'expense_mortgage_you': 'expenses_mortgage_your_partner',
    'expense_mortgage_partner': 'expenses_mortgage_partner',
    'expense_mortgage_freq': 'expenses_mortgage_frequency',
    'expense_board_you': 'expenses_board_your_partner',
    'expense_board_partner': 'expenses_board_partner',
    'expense_board_freq': 'expenses_board_frequency',
    'expense_rates_you': 'expenses_rates_your_partner',
    'expense_rates_partner': 'expenses_rates_partner',
    'expense_rates_freq': 'expenses_rates_frequency',
    'expense_business_exp_you': 'expenses_business_expenses_you',
    'expense_business_exp_partner': 'expenses_business_expenses_partner',
    'expense_business_exp_freq': 'expenses_business_expenses_frequency',
    'expense_childcare_you': 'expenses_child_care_you',
    'expense_childcare_partner': 'expenses_child_care_partner',
    'expense_childcare_freq': 'expenses_child_care_frequency',
    'expense_maintenance_you': 'expenses_child_support_spousal_maintenance_you',
    'expense_maintenance_partner': 'expenses_child_support_spousal_maintenance_partner',
    'expense_maintenance_freq': 'expenses_child_support_spousal_maintenance_frequency',
    'expense_total_you': 'expenses_total_you',
    'expense_total_partner': 'expenses_total_partner',
    'expense_total_freq': 'expenses_total_frequency',
    // Assets
    'asset_home_you': 'assets_home_you',
    'asset_home_partner': 'assets_home_partner',
    'asset_home_mortgage_you': 'assets_home_mortgage_you',
    'asset_home_mortgage_partner': 'assets_home_mortgage_partner',
    'asset_real_estate_you': 'assets_other_real_estate_you',
    'asset_real_estate_partner': 'assets_other_real_estate_partner',
    'asset_other_mortgage_you': 'assets_other_mortgage_you',
    'asset_other_mortgage_partner': 'assets_other_mortgage_partner',
    'asset_farm_you': 'assets_farm_or_business_you',
    'asset_farm_partner': 'assets_farm_or_business_partner',
    'asset_farm_mortgage_you': 'assets_farm_or_business_mortgage_you',
    'asset_farm_mortgage_partner': 'assets_farm_or_business_mortgage_partner',
    'asset_vehicle_you': 'assets_motor_vehicle_you',
    'asset_vehicle_partner': 'assets_motor_vehicle_your_partner',
    'asset_vehicle_loan_you': 'assets_motor_vehicle_loan_you',
    'asset_vehicle_loan_partner': 'assets_motor_vehicle_loan_your_partner',
    'asset_cash_you': 'assets_cash_savings_you',
    'asset_cash_partner': 'assets_cash_savings_your_partner',
    'asset_other_you': 'assets_other_please_specify_you',
    'asset_other_partner': 'assets_other_please_specify_your_partner',
    'asset_total_you': 'assets_total_you',
    'asset_total_partner': 'assets_total_your_partner',
    'property_address': 'homeowner_property_address_line_1',
    'property_names': 'homeowner_property_title_names_line_1',
    'biz_details': 'business_details_if_yes_line_1',
    'op_dob': 'other_party_dob',
    // Page 9 (Other Party)
    'op_institution_name': 'other_party_institution_name',
    'op_first_name': 'other_party_first_name',
    'op_last_name': 'other_party_last_name',
    'op_phone': 'other_party_phone',
    'op_mobile': 'other_party_mobile',
    'op_email': 'other_party_email',
    'op_address': 'other_party_address',
    'op_lawyer_firm': 'other_party_lawyer_firm_name',
    'op_lawyer_phone': 'other_party_lawyer_phone',
    'op_lawyer_fax': 'other_party_lawyer_fax',
    'op_lawyer_email': 'other_party_lawyer_email',
    'op_lawyer_address': 'other_party_lawyer_postal_address',
    // Page 10 (Court)
    'court_tribunal': 'court_or_tribunal_name',
    'court_number': 'court_proceedings_number',
    'preferred_lawyer': 'lawyer_preferred_name',
    'lawyer_firm_details': 'lawyer_firm_name_and_details',
    'fee_payer_name': 'legal_fees_payer_name',
    'fee_payer_relationship': 'legal_fees_payer_relationship',
    'fee_amount_paid': 'legal_fees_amount_paid',
    'legal_problem_details': 'legal_problem_details',
    'vla_guideline': 'vla_guideline_identifying_details',
    'application_merits': 'application_merits_details',
    'applicant_detriment': 'refusal_detriment_details',
    'civil_law_specify': 'legal_problem_civil_law_details',
    'date_of_birth': 'patient_dob',
    'custody_date': 'date_remanded_into_custody',
    'convictions_details': 'prior_convictions_details_line_1',
    'hearing_type_other': 'hearing_type_other_details',
    'appeal_details': 'appeal_details',
    'appeal_court': 'appeal_decision_court',
    'declaration_name': 'applicant_declaration_name',
    'declaration_of': 'applicant_declaration_of',
    'marriage_date': 'marriage_date',
    'defacto_date': 'de_facto_relationship_start_date',
    'separation_date': 'separation_date',
    'divorce_date': 'divorce_date',
    // 27. Criminal Law
    'prior_convictions_year': 'prior_convictions_year',
    'prior_convictions_offence': 'prior_convictions_offence',
    'prior_convictions_penalty': 'prior_convictions_penalty',
    'appeal_date': 'appeal_original_decision_date',
};

const CHECKBOX_MAPS = {
    'title': {
        'Mrs': 'client_title_mrs',
        'Ms': 'client_title_ms',
        'Miss': 'client_title_miss',
        'Master': 'client_title_master',
        'Dr': 'client_title_dr',
        'Mr/Mrs': 'client_title_mr_mrs',
        'Estate of': 'client_title_estateof',
        'Mx': 'client_title_mx',
        'Mr': 'client_title_mr'
    },
    'gender': {
        'Male': 'client_gender_male',
        'Female': 'client_gender_female',
        'Self-described': 'client_gender_selfdescribed',
        'Trans or gender diverse': 'client_gender_trans',
        'Prefer not to say': 'client_gender_prefernottosay'
    },
    'atsi_origin': {
        'No': 'client_abtsi_no',
        'Yes, Aboriginal': 'client_abtsi_yes_ab',
        'Yes, Torres Strait Islander': 'client_abtsi_yes_tsi',
        'Yes, Aboriginal and Torres Strait Islander': 'client_abtsi_yes_abtsi'
    },
    'homeless': { 'Yes': 'client_homeless_yes', 'No': 'client_homeless_no' },
    'currently_in_custody': { 'Yes': 'client_custody_yes', 'No': 'client_custody_no' },
    'dob_estimate': { 'Yes': 'client_dobest_yes', 'No': 'client_dobest_no' },
    'contact_email_pref': { 'Yes': 'client_email_yes', 'No': 'client_email_no' },
    'correspondence_lawyer': { 'Yes': 'client_lawyer_corro_yes', 'No': 'client_lawyer_corro_no' },
    'other_names_used': { 'Yes': 'client_othername_yes', 'No': 'client_othername_no' },
    'other_language': { 'Yes': 'client_lote_yes', 'No': 'client_lote_no' },
    'need_interpreter': { 'Yes': 'client_interpreter_yes', 'No': 'client_interpreter_no' },
    'has_disability': { 'Yes': 'client_disability_yes', 'No': 'client_disability_no', 'Not disclosed': 'client_disability_not_disclosed' },
    'employment_status': {
        'Full time': 'client_employment_fulltime',
        'Part time': 'client_employment_parttime',
        'Casual': 'client_employment_casual',
        'Not employed': 'client_employment_notemployed',
        'Self-employed': 'client_employment_selfemployed'
    },
    'receives_benefit': { 'Yes': 'client_centrelink_yes', 'No': 'client_centrelink_no' },
    'max_benefit_rate': { 'Yes': 'client_maxben_yes', 'No': 'client_maxben_no' },
    'has_partner': { 'Yes': 'partner_yes', 'No': 'partner_no' },
    'partner_benefit': { 'Yes': 'partner_receive_centrelink_yes', 'No': 'partner_receive_centrelink_no' },
    'partner_max_benefit': { 'Yes': 'partner_maxben_yes', 'No': 'partner_maxben_no' },
    'has_dependants': { 'Yes': 'dependants_live_yes', 'No': 'dependants_live_no' },
    'has_other_parties': { 'Yes': 'parties_other_yes', 'No': 'parties_other_no' },
    'fees_paid': { 'Yes': 'legal_fees_paid_yes', 'No': 'legal_fees_paid_no' },
    'is_homeowner': { 'Yes': 'client_homeowner_yes', 'No': 'client_homeowner_no' },
    'assets_seized': { 'Yes': 'retrained_assets_yes', 'No': 'retrained_assets_no' },
    'family_violence': { 'Yes': 'client_fv_yes', 'No': 'client_fv_no' },
    'pays_child_support': { 'Yes': 'childsupport_yes', 'No': 'childsupport_no' },
    'has_family_children': { 'Yes': 'children_relevant_yes', 'No': 'children_relevant_no' },
    'appeal_sentence': { 'Yes': 'appeal_sentence_yes', 'No': 'appeal_sentence_no' },
    'fdr': { 'Yes': 'attended_fdr_yes', 'No': 'attended_fdr_no' }
};

const DISABILITY_VALUE_MAP = {
  'mental_health': 'mental_health_issues',
  'speech_sensory': 'speech_and_sensory',
  'blind_vision': 'blind_or_vision_impaired',
  'deaf_hearing': 'deaf_or_hearing_impaired',
};

const injectData = (pdfFieldName, value, pdfDoc, form, helveticaFont, fieldEntries, annotToPage, pageRefMap, strict = false) => {
  if (value === undefined || value === null || value === "" || !pdfFieldName) return false;
  const v = String(value);
  const fnNorm = normalize(pdfFieldName);
  const lowerV = v.toLowerCase();

  let matches = fieldEntries.filter(e => normalize(e.name) === fnNorm);
  if (!strict && matches.length === 0) {
    matches = fieldEntries.filter(e => {
      const eNorm = normalize(e.name);
      if (fnNorm.length > 5 && eNorm.includes(fnNorm)) {
        const suffix = eNorm.slice(eNorm.indexOf(fnNorm) + fnNorm.length);
        if (suffix) {
          const subSuffixes = ['day', 'month', 'year', 'line', 'selfdescribed', 'other', 'details', 'specify', 'facility', 'date', 'id', 'estimate'];
          if (subSuffixes.some(sub => suffix.startsWith(sub))) {
            return false;
          }
        }
        return true;
      }
      return false;
    });
  }

  if (matches.length === 0) return false;

  let filled = false;
  matches.forEach(match => {
    let f;
    try {
      f = form.getField(match.name);
      if (isPDFTextField(f)) {
        f.setText(v);
        filled = true;
      } else if (isPDFCheckBox(f)) {
        const isYes = ['yes', 'on', 'true', '1'].includes(lowerV);
        if (isYes || normalize(match.name).includes(lowerV)) {
          try { f.check(); } catch (e) { }
          filled = true;
        }
      } else if (isPDFRadioGroup(f)) {
        try {
          const options = f.getOptions();
          if (options.includes(v)) {
            f.select(v);
          } else if (v === "Yes" || v === "on") {
            f.select(options[0]);
          } else if (v === "No") {
            f.select(options[1]);
          }
          filled = true;
        } catch (e) { }
      }
    } catch (e) { }

    // Visual Painting Fallback removed. PDF is now flattened before save.
  });
  return filled;
};

export async function generateVLAPdf(formData) {
  try {
    // 1. Try local dev backend first (if running)
    try {
      const response = await fetch('http://localhost:3001/api/fill-vla', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        const bytes = await response.arrayBuffer();
        return new Uint8Array(bytes);
      }
    } catch (e) {
      console.warn("Backend PDF generation failed, using local fallback...");
    }

    // 2. Browser Fallback Engine
    // Work on a copy. The totals worked out below are for the PDF only; writing them back
    // into the caller's answers changed the application behind the lawyer's back, which
    // made a just-certified application read as "changed since it was certified".
    formData = { ...formData };
    const response = await fetch(`/vla-fillable-form.pdf?t=${Date.now()}`);
    if (!response.ok) throw new Error("Template fetch failed");
    const existingPdfBytes = await response.arrayBuffer();

    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const zapfFont = await pdfDoc.embedFont(StandardFonts.ZapfDingbats);
    const allPages = pdfDoc.getPages();
    const form = pdfDoc.getForm();



    try {
      const acroForm = pdfDoc.catalog.getOrCreateAcroForm();
      acroForm.dict.set(PDFName.of('NeedAppearances'), PDFBool.True);
      
      let dr = acroForm.dict.get(PDFName.of('DR'));
      if (!dr) {
        dr = pdfDoc.context.obj({});
        acroForm.dict.set(PDFName.of('DR'), dr);
      }
      let fontDict = dr.lookup(PDFName.of('Font'));
      if (!fontDict || !(fontDict instanceof PDFDict)) {
        fontDict = pdfDoc.context.obj({});
        dr.set(PDFName.of('Font'), fontDict);
      }
      fontDict.set(PDFName.of('Helv'), helveticaFont.ref);
      fontDict.set(PDFName.of('Helvetica'), helveticaFont.ref);
      fontDict.set(PDFName.of('ZaDb'), zapfFont.ref);
    } catch (e) { }

    const fieldEntries = [];
    const annotToPage = new Map();
    const pageRefMap = new Map();

    allPages.forEach(p => {
      const pRef = pdfDoc.context.getObjectRef(p.node);
      if (pRef) pageRefMap.set(pRef.toString(), p);
      const annots = pdfDoc.context.lookup(p.node.get(PDFName.of('Annots')));
      if (annots instanceof PDFArray) {
        for (let i = 0; i < annots.size(); i++) {
          const ref = annots.get(i);
          if (ref) annotToPage.set(ref.toString(), p);
        }
      }
    });

    const allObjects = pdfDoc.context.enumerateIndirectObjects();
    for (let i = 0; i < allObjects.length; i++) {
      if (i % 500 === 0) await new Promise(r => setTimeout(r, 0));
      const [ref, obj] = allObjects[i];
      if (obj instanceof PDFDict) {
        let nameObj = pdfDoc.context.lookup(obj.get(PDFName.of('T')));
        if (!nameObj) {
          const parent = pdfDoc.context.lookup(obj.get(PDFName.of('Parent')));
          if (parent instanceof PDFDict) {
            nameObj = pdfDoc.context.lookup(parent.get(PDFName.of('T')));
          }
        }

        if (nameObj) {
          let n = "";
          if (nameObj instanceof PDFName || nameObj instanceof PDFString || nameObj instanceof PDFHexString) {
            n = nameObj.asString();
          } else {
            n = String(nameObj);
          }
          if (n && !fieldEntries.find(fe => fe.name === n)) {
            fieldEntries.push({ name: n, dict: obj, refStr: ref.toString() });
          }
        }
      }
    }

    const allFields = form.getFields();

    const fill = (name, val, strict = false) => injectData(name, val, pdfDoc, form, helveticaFont, fieldEntries, annotToPage, pageRefMap, strict);

    // Force Global Font Sizes and Custom Checkbox Styles (same as server.js)
    allFields.forEach(field => {
      try {
        if (isPDFTextField(field)) {
          field.acroField.setDefaultAppearance('/Helv 12 Tf 0 g');
        } else if (isPDFCheckBox(field)) {
          field.acroField.setDefaultAppearance('/ZaDb 12 Tf 0 g');
          field.acroField.getWidgets().forEach(w => {
            let mk = w.dict.lookup(PDFName.of('MK'));
            if (!mk) { mk = pdfDoc.context.obj({}); w.dict.set(PDFName.of('MK'), mk); }
            mk.set(PDFName.of('CA'), PDFString.of('l')); // Circle
          });
        } else if (isPDFRadioGroup(field)) {
          field.acroField.setDefaultAppearance('/ZaDb 12 Tf 0 g');
          field.acroField.getWidgets().forEach(w => {
            let mk = w.dict.lookup(PDFName.of('MK'));
            if (!mk) { mk = pdfDoc.context.obj({}); w.dict.set(PDFName.of('MK'), mk); }
            mk.set(PDFName.of('CA'), PDFString.of('4')); // Check
          });
        }
      } catch (e) { }
    });

    // --- THE ASSET TOTALIZER ---
    const sum = (v1, v2) => {
        const n1 = parseFloat(v1) || 0;
        const n2 = parseFloat(v2) || 0;
        const total = n1 + n2;
        return total > 0 ? total.toString() : "";
    };
    formData.assets_motor_vehicle = sum(formData.asset_vehicle_you, formData.asset_vehicle_partner);
    formData.assets_motor_vehicle_loan = sum(formData.asset_vehicle_loan_you, formData.asset_vehicle_loan_partner);
    formData.assets_cash_savings = sum(formData.asset_cash_you, formData.asset_cash_partner);
    formData.assets_total = sum(formData.asset_total_you, formData.asset_total_partner);

    // --- PRINT ENFORCER ---
    allFields.forEach(field => {
        field.acroField.getWidgets().forEach(w => w.dict.set(PDFName.of('F'), PDFNumber.of(4)));
    });

    // 1. TEXT MAPPING
    for (const [webKey, value] of Object.entries(formData)) {
        if (!value) continue;
        // Skip dates and addresses from general text mapping to prevent duplicate painting or incorrect parent fields
        if (webKey.includes('date') || webKey.includes('dob') || webKey === 'home_address' || webKey === 'postal_address') {
            continue;
        }
        const pdfKey = TRANSLATION_MAP[webKey] || webKey;
        fill(pdfKey, value, true); // strict matching for general text mapping
    }

    // 2. THE TOTAL CHECKBOX GEARBOX
    const fuzzyCheck = (prefix, pattern) => {
        if (!pattern || !prefix) return;
        const cleanPattern = String(pattern).toLowerCase().replace(/[^a-z0-9]/g, '');
        const cleanPrefix = String(prefix).toLowerCase().replace(/[^a-z0-9]/g, '');
        
        allFields.forEach(f => {
            const rawName = f.getName().toLowerCase();
            const cleanName = rawName.replace(/[^a-z0-9]/g, '');
            
            if (cleanName.includes(cleanPrefix) && cleanName.includes(cleanPattern)) {
                try {
                    if (isPDFCheckBox(f)) {
                        f.check();
                        const onValue = f.acroField.getWidgets()[0]?.getOnValue() || PDFName.of('Yes');
                        f.acroField.dict.set(PDFName.of('V'), onValue);
                        f.acroField.getWidgets().forEach(w => w.dict.set(PDFName.of('AS'), onValue));
                    } else {
                        f.acroField.dict.set(PDFName.of('V'), PDFName.of('Yes'));
                        f.acroField.getWidgets().forEach(w => w.dict.set(PDFName.of('AS'), PDFName.of('Yes')));
                    }
                } catch (e) { }
            }
        });
    };

    const check = (name) => {
        const field = allFields.find(f => f.getName().toLowerCase() === name.toLowerCase());
        if (field) {
            try {
                if (isPDFCheckBox(field)) {
                    field.check();
                    const onValue = field.acroField.getWidgets()[0]?.getOnValue() || PDFName.of('Yes');
                    field.acroField.dict.set(PDFName.of('V'), onValue);
                    field.acroField.getWidgets().forEach(w => w.dict.set(PDFName.of('AS'), onValue));
                } else {
                    field.acroField.dict.set(PDFName.of('V'), PDFName.of('Yes'));
                    field.acroField.getWidgets().forEach(w => w.dict.set(PDFName.of('AS'), PDFName.of('Yes')));
                }
            } catch (e) { }
        }
    };

    const setRadio = (fieldStr, value) => {
        try {
            const field = form.getField(fieldStr);
            if (isPDFRadioGroup(field)) {
                const options = field.getOptions();
                if (value === 'Yes' || value === 'on' || value === true) {
                    field.select(options[0]);
                } else if (value === 'No' || value === false) {
                    field.select(options[1]);
                }
            }
        } catch (e) { }
    };

    for (const [webKey, mapConfig] of Object.entries(CHECKBOX_MAPS)) {
        const val = formData[webKey];
        if (!val) continue;
        const targetCheckboxName = mapConfig[val];
        if (targetCheckboxName) {
            check(targetCheckboxName);
        }
    }

    if (formData.english_level) fuzzyCheck('i_english_proficiency', formData.english_level.replace(/ /g, '_'));
    if (Array.isArray(formData.disability_types)) formData.disability_types.forEach(t => fuzzyCheck('i_disability_type', t === 'mental_health' ? 'mental_health_issues' : t));
    
    const SUPPORT_MAP = {
        'carer_present': 'i_support_type_carer_or_third_party_present',
        'quiet_space': 'i_support_type_quiet_space',
        'plain_english': 'i_support_type_plain_english',
        'screen_reader': 'i_support_type_screen_reader',
        'building_access': 'i_support_type_access_to_buildings',
        'text_phone': 'i_support_type_text_phone',
        'hearing_loop': 'i_support_type_hearing_loop',
        'video_conference': 'i_support_type_video_conference',
        'low_lighting': 'i_support_type_low_lighting',
        'auslan': 'i_support_type_auslan_interpreter',
    };
    if (Array.isArray(formData.support_types)) {
        formData.support_types.forEach(t => {
            if (SUPPORT_MAP[t]) check(SUPPORT_MAP[t]);
        });
    }
    if (Array.isArray(formData.benefit_types)) {
        formData.benefit_types.forEach(t => {
            if (t === 'Parenting payment') check('i_centrelink_benefit_type_carer_payment_1');
            else fuzzyCheck('i_centrelink_benefit_type', t.replace(/ /g, '_'));
        });
    }
    if (Array.isArray(formData.partner_benefit_types)) formData.partner_benefit_types.forEach(t => fuzzyCheck('i_partner_benefit_type', t.replace(/ /g, '_')));
    if (Array.isArray(formData.relevant_factors)) formData.relevant_factors.forEach(t => fuzzyCheck('i_legal_problem_factor', t));
    if (formData.relationship_status) fuzzyCheck('i_relationship_status', formData.relationship_status.replace(/ /g, '_'));

    // Section 13: Dependants
    if (Array.isArray(formData.dependants)) {
        formData.dependants.slice(0, 6).forEach((dep, i) => {
            const idx = i + 1;
            if (dep.first_name) fill(`dependant_${idx}_first_name`, dep.first_name, true);
            if (dep.last_name) fill(`dependant_${idx}_last_name`, dep.last_name, true);
            if (dep.relationship) fill(`dependant_${idx}_relationship_to_you`, dep.relationship, true);
            if (dep.dob && dep.dob.includes('-')) {
                const p = dep.dob.split('-');
                fill(`dependant_${idx}_date_of_birth_day`, p[2], true);
                fill(`dependant_${idx}_date_of_birth_month`, p[1], true);
                fill(`dependant_${idx}_date_of_birth_year`, p[0], true);
            }
        });
    }

    // Section 28: Existing Orders
    if (formData.existing_orders) {
        if (formData.existing_orders === 'Yes, interim') check('i_existing_orders_yes_interim');
        else if (formData.existing_orders === 'Yes, final') check('i_existing_orders_yes_final');
        else if (formData.existing_orders === 'No') check('i_existing_orders_no');
    }

    // Criminal Law (Prior Convictions)
    if (formData.prior_convictions === 'Yes') check('prior_convictions_yes');
    else if (formData.prior_convictions === 'No') check('prior_convictions_no');

    if (formData.appeal_conviction === 'Yes') check('appeal_conviction_yes');
    else if (formData.appeal_conviction === 'No') check('appeal_conviction_no');

    // Other Parties
    if (formData.other_party_type) {
        if (formData.other_party_type === 'A person') fuzzyCheck('i_other_party_type', 'person');
        else fuzzyCheck('i_other_party_type', 'institution');
    }
    if (formData.op_title) {
        const OP_TITLE_MAP = {
            'Mr': 'i_other_party_title_mr',
            'Mrs': 'i_other_party_title_mrs',
            'Miss': 'i_other_party_title_miss',
            'Ms': 'i_other_party_title_ms',
            'Master': 'i_other_party_title_master',
            'Mr/Mrs': 'i_other_party_title_mr_mrs',
            'Estate of': 'i_other_party_title_estate_of',
            'Dr': 'i_other_party_title_dr',
            'Mx': 'i_other_party_title_mx'
        };
        
        Object.values(OP_TITLE_MAP).forEach(fieldName => {
            try {
                const f = form.getField(fieldName);
                if (f && isPDFCheckBox(f)) f.uncheck();
            } catch(e) {}
        });

        if (OP_TITLE_MAP[formData.op_title]) {
            check(OP_TITLE_MAP[formData.op_title]);
        }
    }
    if (formData.op_gender) {
        try {
            const opGenderField = form.getField('other_party_gender');
            if (opGenderField && isPDFRadioGroup(opGenderField)) {
                if (formData.op_gender === 'Male') opGenderField.select('Choice1');
                else if (formData.op_gender === 'Female') opGenderField.select('Choice2');
                else if (formData.op_gender === 'Self-described') opGenderField.select('Choice3');
                else if (formData.op_gender === 'Not applicable') opGenderField.select('Choice4');
            }
        } catch (e) {}
    }
    if (formData.op_dob && formData.op_dob.includes('-')) {
        const p = formData.op_dob.split('-');
        fill('other_party_dob_year', p[0], true);
        fill('other_party_dob_month', p[1], true);
        fill('other_party_dob_day', p[2], true);
    }
    if (formData.op_relationship) fuzzyCheck('i_other_party_relationship', formData.op_relationship.toLowerCase().replace(/ /g, '_').replace(/\//g, '_'));
    if (formData.op_role) fuzzyCheck('i_other_party_role', formData.op_role.toLowerCase().replace(/ /g, '_').replace(/\//g, '_'));

    // Legal Problem Type (Law Type)
    if (formData.law_type) {
        fuzzyCheck('i_legal_problem_type', formData.law_type.toLowerCase().replace(/ /g, '_'));
    }

    // Family Law / Children
    if (formData.family_children) {
        formData.family_children.forEach((child, i) => {
            const idx = i + 1;
            if (child.first_name) {
                fill(`child_${idx}_first_name`, child.first_name, true);
                fill(`child_${idx}_first_name_1`, child.first_name, true);
            }
            if (child.middle_name) fill(`child_${idx}_middle_name`, child.middle_name, true);
            if (child.last_name) fill(`child_${idx}_last_name`, child.last_name, true);
            if (child.gender) fill(`child_${idx}_sex`, child.gender, true);
            if (child.relationship) fill(`child_${idx}_relationship_to_you`, child.relationship, true);
            if (child.lives_with) {
                fill(`child_${idx}_lives_with`, child.lives_with, true);
                fill(`child_${idx}_lives_with_1`, child.lives_with, true);
            }
            if (child.dob && child.dob.includes('-')) {
                const p = child.dob.split('-');
                fill(`child_${idx}_date_of_birth_day`, p[2], true);
                fill(`child_${idx}_date_of_birth_month`, p[1], true);
                fill(`child_${idx}_date_of_birth_year`, p[0], true);
            }
            if (child.since_when && child.since_when.includes('-')) {
                const p = child.since_when.split('-');
                fill(`child_${idx}_since_when_day`, p[2], true);
                fill(`child_${idx}_since_when_month`, p[1], true);
                fill(`child_${idx}_since_when_year`, p[0], true);
            }
        });
    }

    // Family Law Orders
    if (formData.order_date && formData.order_date.includes('-')) {
        const p = formData.order_date.split('-');
        fill('existing_order_date_year', p[0], true);
        fill('existing_order_date_month', p[1], true);
        fill('existing_order_date_day', p[2], true);
    }

    const COURT_MAP = {
        'Children\'s Court': 'order_made_by_court_childrens_court',
        'Family Court': 'order_made_by_court_family_court',
        'Federal Circuit Court': 'order_made_by_court_federal_magistrates_court',
        'Magistrates\' Court of Victoria': 'order_made_by_court_magistrates_court_of_Victoria',
        'Other': 'order_made_by_court_other'
    };
    const orderCourt = formData.order_court || formData.order_made_by;
    if (orderCourt && COURT_MAP[orderCourt]) check(COURT_MAP[orderCourt]);

    const ORDER_TYPE_MAP = {
        'Family law': 'i_order_type_family_law',
        'Child support': 'i_order_type_child_support',
        'Family violence': 'i_order_type_family_violence',
        'Child welfare': 'i_order_type_child_welfare'
    };
    if (Array.isArray(formData.order_types)) {
        formData.order_types.forEach(t => {
            if (ORDER_TYPE_MAP[t]) check(ORDER_TYPE_MAP[t]);
        });
    }

    if (formData.lived_with_op === 'Yes') check('lived_with_other_party_yes');
    else if (formData.lived_with_op === 'No') check('lived_with_other_party_no');

    // Means Waiver & Checklist
    if (formData.waiver_proof_means) setRadio('proof_of_means_waiver', formData.waiver_proof_means);

    const WAIVER_MAP = {
        'custody_savings': 'i_waiver_basis_in_custody_savings_investments_le_1095',
        'custody_bail': 'i_waiver_basis_in_custody_bail_application_assistance',
        'custody_summary': 'i_waiver_basis_in_custody_summary_crime_within_seven_days',
        'atsi': 'i_waiver_basis_aboriginal_or_torres_strait_islander_origin',
        'family_violence': 'i_waiver_basis_family_violence',
        'homeless': 'i_waiver_basis_homeless',
        'remote': 'i_waiver_basis_remote_location'
    };
    if (Array.isArray(formData.waiver_basis)) {
        formData.waiver_basis.forEach(w => {
            if (WAIVER_MAP[w]) check(WAIVER_MAP[w]);
        });
    }

    const CHECKLIST_MAP = {
        'answered_all': 'i_checklist_answered_all_relevant_questions',
        'financial_docs': 'i_checklist_attached_financial_documents',
        'legal_docs': 'i_checklist_attached_other_court_legal_documents',
        'signed_declaration': 'i_checklist_signed_applicant_declaration'
    };
    if (Array.isArray(formData.application_checklist)) {
        formData.application_checklist.forEach(c => {
            if (CHECKLIST_MAP[c]) check(CHECKLIST_MAP[c]);
        });
    }

    // Safety Fears - Radio Groups
    if (formData.fear_safety) setRadio('fear_for_your_safety', formData.fear_safety);
    if (formData.fear_children_safety) setRadio('fear_for_childrens_safety', formData.fear_children_safety);
    if (formData.child_abuse_investigation) setRadio('child_abuse_investigation', formData.child_abuse_investigation);

    // Family Violence Checkboxes
    if (formData.fvo_you === 'No') check('i_family_violence_order_for_protection_no');
    else if (formData.fvo_you === 'Yes, interim') check('i_family_violence_order_for_protection_yes_interim');
    else if (formData.fvo_you === 'Yes, final') check('i_family_violence_order_for_protection_yes_final');

    if (formData.fvo_children === 'No') check('i_family_violence_order_for_children_protection_no');
    else if (formData.fvo_children === 'Yes, interim') check('i_family_violence_order_for_children_protection_yes_interim');
    else if (formData.fvo_children === 'Yes, final') check('i_family_violence_order_for_children_protection_yes_final');

    const FVO_ROLE_MAP = {
        'Applicant/plaintiff/appellant': 'i_fv_order_role_applicant_plaintiff_appellant',
        'Accused/defendant/respondent': 'i_fv_order_role_accused_defendant_respondent',
        'Interested party': 'i_fv_order_role_interested_party',
        'No court proceedings': 'i_fv_order_role_no_court_proceedings',
        'Other': 'i_fv_order_role_other'
    };
    if (formData.fvo_children_role && FVO_ROLE_MAP[formData.fvo_children_role]) {
        check(FVO_ROLE_MAP[formData.fvo_children_role]);
    }

    // Signatures and Dates
    const appSigDate = formData.client_signature_date || formData.lawyer_date;
    if (appSigDate && appSigDate.includes('-')) {
        const p = appSigDate.split('-');
        fill('applicant_signature_date_year', p[0], true);
        fill('applicant_signature_date_month', p[1], true);
        fill('applicant_signature_date_day', p[2], true);
    }
    
    if (formData.lawyer_date && formData.lawyer_date.includes('-')) {
        const p = formData.lawyer_date.split('-');
        fill('identity_verification_date_year', p[0], true);
        fill('identity_verification_date_month', p[1], true);
        fill('identity_verification_date_day', p[2], true);
    }

    if (formData.lawyer_signed) {
        fill('identity_verification_confirmed_by', formData.lawyer_signed, true);
    }
    if (formData.verbal_consent_method === 'By telephone') check('i_identity_verification_mode_over_the_phone');
    else if (formData.verbal_consent_method === 'In person') check('i_identity_verification_mode_in_person');
    else if (formData.verbal_consent_method === 'Other') check('i_identity_verification_mode_other');

    // Paint Signature Image or Text
    if (formData.client_signature) {
        if (formData.client_signature.startsWith('data:image/')) {
            try {
                const sigField = form.getField('applicant_signature');
                const widgets = sigField.acroField.getWidgets();
                if (widgets.length > 0) {
                    const rect = widgets[0].getRectangle();
                    const base64Data = formData.client_signature.split(',')[1];
                    const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
                    const sigImage = await pdfDoc.embedPng(imageBytes);
                    const pages = pdfDoc.getPages();
                    const pageRef = widgets[0].P();
                    let targetPage = pages.find(p => p.ref === pageRef) || pages[pages.length - 1];
                    targetPage.drawImage(sigImage, {
                        x: rect.x + 2,
                        y: rect.y + 2,
                        width: rect.width - 4,
                        height: rect.height - 4
                    });
                }
            } catch(e) {
                console.error("Failed to paint signature:", e);
            }
        } else {
            fill('applicant_signature', formData.client_signature, true);
            fill('applicant_declaration_name', formData.client_signature, true);
        }
    }

    if (formData.centrelink_consent_signature) {
        fill('centrelink_consent_name', formData.centrelink_consent_signature, true);
    }

    if (formData.appeal_date && formData.appeal_date.includes('-')) {
        const p = formData.appeal_date.split('-');
        fill('appeal_original_decision_date_year', p[0], true);
        fill('appeal_original_decision_date_month', p[1], true);
        fill('appeal_original_decision_date_day', p[2], true);
    }
    
    if (formData.centrelink_benefit_type === 'Jobseeker' || formData.centrelink_benefit_type === 'Newstart') check('i_centrelink_benefit_type_jobseeker');
    else if (formData.centrelink_benefit_type === 'Youth Allowance') check('i_centrelink_benefit_type_youth_allowance');
    else if (formData.need_support === 'Not disclosed') check('i_requires_support_not_disclosed');
    
    // Court Proceedings Mappings
    if (formData.has_proceedings === 'No') check('i_court_proceedings_no');
    else if (formData.has_proceedings === 'Yes, current') check('i_court_proceedings_yes_current');
    else if (formData.has_proceedings === 'Yes, intended') check('i_court_proceedings_yes_intended');

    if (formData.hearing_date && formData.hearing_date.includes('-')) {
        const p = formData.hearing_date.split('-');
        fill('next_hearing_date_year', p[0], true);
        fill('next_hearing_date_month', p[1], true);
        fill('next_hearing_date_day', p[2], true);
    }

    const ROLE_MAP = {
        'Applicant/plaintiff/appellant': 'i_proceedings_role_applicant_plaintiff_appellant',
        'Accused/defendant/respondent': 'i_proceedings_role_accused_defendant_respondent',
        'Interested party': 'i_proceedings_role_interested_party',
        'No court proceedings': 'i_proceedings_role_no_court_proceedings',
        'Other': 'i_proceedings_role_other'
    };
    if (formData.proceedings_role && ROLE_MAP[formData.proceedings_role]) {
        check(ROLE_MAP[formData.proceedings_role]);
    }

    const HEARING_MAP = {
        'Appeal': 'i_hearing_type_appeal',
        'Summary hearing': 'i_hearing_type_summary',
        'Indictable hearing': 'i_hearing_type_indictable',
        'Family law': 'i_hearing_type_family_law',
        'Civil application': 'i_hearing_type_civil_application',
        'Mediation/alternative dispute resolution': 'i_hearing_type_mediation_alternative_dispute_resolution',
        'No hearing': 'i_hearing_type_no_hearing',
        'Other': 'i_hearing_type_other'
    };
    if (formData.hearing_type && HEARING_MAP[formData.hearing_type]) {
        check(HEARING_MAP[formData.hearing_type]);
    }

    if (formData.title === 'Mr') {
        check('i_patient_title_mr');
    }

    // 4. MULTI-LINE SUMMARY BLOCKS
    const splitIntoLines = (text, prefix, maxLines) => {
        if (!text) return;
        const words = text.split(' ');
        let currentLine = "";
        let lineIdx = 1;
        words.forEach(word => {
            if ((currentLine + word).length > 80 && lineIdx < maxLines) {
                fill(`${prefix}${lineIdx}`, currentLine, true);
                currentLine = word + " ";
                lineIdx++;
            } else {
                currentLine += word + " ";
            }
        });
        fill(`${prefix}${lineIdx}`, currentLine, true);
    };
    splitIntoLines(formData.legal_problem_details, 'legal_problem_details_', 4);
    splitIntoLines(formData.vla_guideline, 'vla_guideline_details_', 5);
    splitIntoLines(formData.application_merits, 'application_merits_', 5);
    splitIntoLines(formData.applicant_detriment, 'refusal_detriment_details_', 5);
    splitIntoLines(formData.fee_payer_reason, 'legal_fees_reason_cannot_continue_', 3);
    splitIntoLines(formData.fdr_reasons, 'fdr_non_attendance_reason_', 4);

    // 5. DEPENDANTS (SECTION 13)
    if (Array.isArray(formData.dependants)) {
        formData.dependants.slice(0, 6).forEach((dep, i) => {
            const idx = i + 1;
            fill(`dependant_${idx}_first_name`, dep.first_name || "", true);
            fill(`dependant_${idx}_last_name`, dep.last_name || "", true);
            fill(`dependant_${idx}_relationship`, dep.relationship || "", true);

            // Dependant DOB splitting
            if (dep.dob && dep.dob.includes('-')) {
                const p = dep.dob.split('-');
                fill(`dependant_${idx}_dob_day`, p[2], true);
                fill(`dependant_${idx}_dob_month`, p[1], true);
                fill(`dependant_${idx}_dob_year`, p[0], true);
            }
        });
    }

    // 6. DATE SPLITTING (THE CHRONO-DISTRIBUTOR)
    const splitDate = (dateVal, prefix) => {
        if (!dateVal) return;
        const p = dateVal.split('-');
        if (p.length === 3) {
            fill(`${prefix}_day`, p[2], true);
            fill(`${prefix}_month`, p[1], true);
            fill(`${prefix}_year`, p[0], true);
        }
    };

    // Process all dates in formData
    for (const [key, val] of Object.entries(formData)) {
        if ((key.includes('date') || key.includes('dob')) && val && String(val).includes('-')) {
            const pdfPrefix = TRANSLATION_MAP[key] || key;
            splitDate(val, pdfPrefix);
        }
    }

    if (formData.home_address) {
      const homeLines = splitTextIntoLines(formData.home_address, 45, 3);
      fill('patient_home_address_line_1', homeLines[0], true);
      fill('patient_home_address_line_2', homeLines[1], true);
      fill('patient_home_address_line_3', homeLines[2], true);
    }

    if (formData.postal_address) {
      const postalLines = splitTextIntoLines(formData.postal_address, 45, 3);
      fill('patient_postal_address_line_1', postalLines[0], true);
      fill('patient_postal_address_line_2', postalLines[1], true);
      fill('patient_postal_address_line_3', postalLines[2], true);
    }

    // Business & Trust Checkboxes (RadioGroups with Choice1/Choice2)
    const setBizRadio = (fieldStr, value) => {
      try {
        const field = form.getField(fieldStr);
        if (field && isPDFRadioGroup(field)) {
          if (value === 'Yes') field.select('Choice1');
          else if (value === 'No') field.select('Choice2');
        }
      } catch (e) {}
    };
    setBizRadio('business_self_employed', formData.biz_self_employed);
    setBizRadio('business_partner_or_director', formData.biz_director);
    setBizRadio('business_shareholder_private_company', formData.biz_shareholder);
    setBizRadio('money_from_trust', formData.biz_trust);
    setBizRadio('business_other_benefit', formData.biz_other_benefit);

    if (formData.biz_self_employed === 'Yes') check('business_self_employed');
    if (formData.biz_partner === 'Yes') check('business_partner_or_director');
    if (formData.biz_shareholder === 'Yes') check('business_shareholder_private_company');
    if (formData.biz_trust === 'Yes') check('money_from_trust');
    if (formData.biz_other === 'Yes') check('business_other_benefit');

    if (formData.op_dob_estimate === 'Yes') check('other_party_dob_estimate_only_yes');
    else if (formData.op_dob_estimate === 'No') check('other_party_dob_estimate_only_no');

    // Force Global Font Sizes and Custom Checkbox Styles (Text: Helvetica 12, Checkboxes: ZapfDingbats 7.5)
    form.getFields().forEach(field => {
      try {
        if (isPDFTextField(field)) {
          field.acroField.setDefaultAppearance('/Helv 12 Tf 0 g');
        } else if (isPDFCheckBox(field) || isPDFRadioGroup(field)) {
          field.acroField.setDefaultAppearance('/ZaDb 7.5 Tf 0 g');
          field.acroField.getWidgets().forEach(w => {
            let mk = w.dict.lookup(PDFName.of('MK'));
            if (!mk) { mk = pdfDoc.context.obj({}); w.dict.set(PDFName.of('MK'), mk); }
            mk.set(PDFName.of('CA'), PDFString.of('4')); // Check mark

            // Resizing override removed so that the PDF template's native checkbox sizes are respected.
          });
        }
      } catch (e) { }
    });

    try {
      form.updateFieldAppearances(helveticaFont);
    } catch (e) {
      console.warn("Appearance updates failed, continuing to flatten:", e);
    }

    try {
      form.flatten();
    } catch (e) {
      console.error("Flattening failed:", e);
    }

    // Clean up AcroForm and Names to remove leftover interactive form elements and JavaScript
    try {
      pdfDoc.catalog.delete(PDFName.of('AcroForm'));
      pdfDoc.catalog.delete(PDFName.of('Names'));
    } catch (e) {
      console.error("AcroForm/Names catalog cleanup failed:", e);
    }

    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
  } catch (err) {
    console.error("PDF GENERATION ERROR:", err);
    throw err;
  }
}